import { EntitySelectConfig, InvoiceFormConfig } from '../InvoiceFormBase/types';
import {
	locationsRestCrudThunksForBuyer,
	locationsRestCrudThunksForSupplier,
} from '../../thunks/locations_thunks';
import {
	fetchSupplierFacilitiesInPrivateNetworkForBuyer,
	fetchSupplierFacilitiesInPrivateNetworkForSupplier,
	supplierFacilitiesRestCrudThunksForBuyer,
	supplierFacilitiesRestCrudThunksForSupplier,
} from '../../thunks/supplier_facilities_thunks';

// The Service Provider and Bill to location pickers a project invoice form needs.
//
// A work-order invoice derives both from its work order; a project invoice has no work
// order, so they become fields on the form and the page has to supply their data
// sources (InvoiceFormBase stays role-agnostic and never reaches for a thunk itself).
//
// Shared by the create page and the edit page. They are the same two pickers against
// the same two collections, so a second copy would only be a copy that drifts on page
// size, role suffix or search-param shape.

export const PROJECT_INVOICE_LOCATIONS_AUTOCOMPLETE = 'PROJECT_INVOICE_LOCATIONS_AUTOCOMPLETE';
export const PROJECT_INVOICE_SUPPLIERS_AUTOCOMPLETE = 'PROJECT_INVOICE_SUPPLIERS_AUTOCOMPLETE';

// A page's list fetcher. Note the first argument: these take a PARAMS OBJECT, where
// EntitySelectConfig['fetchData'] takes the raw search string OWAsyncSelect types into
// it. Converting between the two is the whole job of buildProjectInvoiceEntitySelects —
// they are not interchangeable, and passing a bare string through spreads it into
// per-character query params.
type EntityListFetcher = (
	params: Record<string, any>,
	targetCollectionName?: string,
	pagination?: any,
	sorting?: any,
	filters?: any,
	addToTargetCollection?: boolean
) => any;

// The state and dispatch props a page must hand to buildProjectInvoiceEntitySelects.
// Named so a call-site's own props interface can extend it rather than restate it.
export interface ProjectInvoiceEntitySelectProps {
	// Whole redux slices, not getRecordsForTargetCollection output — OWAsyncSelect reads
	// pagination and fetching off them, and resolves a selected value's label from their
	// records.
	locations: any;
	supplierFacilities: any;
	fetchLocations: EntityListFetcher;
	fetchMultipleLocations: EntitySelectConfig['fetchMultiple'];
	fetchSupplierFacilities: EntityListFetcher;
	fetchMultipleSupplierFacilities: EntitySelectConfig['fetchMultiple'];
}

export const projectInvoiceEntitySelectsStateToProps = (state) => ({
	locations: state.locations,
	supplierFacilities: state.supplier_facilities,
});

// Role-matched fetchers, to be spread into a page's mapDispatchToProps.
export const projectInvoiceEntitySelectsDispatchToProps = (dispatch, isSupplier) => ({
	fetchLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			(isSupplier ? locationsRestCrudThunksForSupplier : locationsRestCrudThunksForBuyer).readLite(
				{ ...params, no_pagination: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleLocations: (ids, targetCollectionName) =>
		dispatch(
			(isSupplier
				? locationsRestCrudThunksForSupplier
				: locationsRestCrudThunksForBuyer
			).readMultiple(ids, targetCollectionName)
		),
	// addToTargetCollection has to be passed through: OWAsyncSelect sends true when it
	// pages, and dropping it made every page replace the previous one instead of
	// appending, so the dropdown could never be scrolled past its first page.
	fetchSupplierFacilities: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			(isSupplier
				? fetchSupplierFacilitiesInPrivateNetworkForSupplier
				: fetchSupplierFacilitiesInPrivateNetworkForBuyer)(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleSupplierFacilities: (ids, targetCollectionName) =>
		dispatch(
			(isSupplier
				? supplierFacilitiesRestCrudThunksForSupplier
				: supplierFacilitiesRestCrudThunksForBuyer
			).readMultiple(ids, targetCollectionName)
		),
});

// The `entitySelects` config InvoiceFormBase consumes, built from those props.
export const buildProjectInvoiceEntitySelects = ({
	locations,
	supplierFacilities,
	fetchLocations,
	fetchMultipleLocations,
	fetchSupplierFacilities,
	fetchMultipleSupplierFacilities,
}: ProjectInvoiceEntitySelectProps): InvoiceFormConfig['entitySelects'] => ({
	supplierFacility: {
		stateSlice: supplierFacilities,
		targetCollectionName: PROJECT_INVOICE_SUPPLIERS_AUTOCOMPLETE,
		fetchMultiple: fetchMultipleSupplierFacilities,
		// OWAsyncSelect hands the raw search string through as the first argument, so it
		// has to become a params object here or the thunk spreads it into per-character
		// query params.
		fetchData: (
			searchText,
			targetCollectionName,
			pagination,
			sorting,
			filters,
			addToTargetCollection
		) =>
			fetchSupplierFacilities(
				{ name: searchText },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			),
	},
	location: {
		stateSlice: locations,
		targetCollectionName: PROJECT_INVOICE_LOCATIONS_AUTOCOMPLETE,
		fetchMultiple: fetchMultipleLocations,
		// Locations search on `search`, supplier facilities on `name` — the two endpoints
		// differ, so the param name is not interchangeable between these two blocks.
		fetchData: (
			searchText,
			targetCollectionName,
			pagination,
			sorting,
			filters,
			addToTargetCollection
		) =>
			fetchLocations(
				{ search: searchText || undefined },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			),
	},
});
